<template>
  <v-content class="grey lighten-4">
    <v-btn small class="mt-10" fab absolute top left @click="$router.go(-1)">
      <v-icon>mdi-arrow-left</v-icon>
    </v-btn>
    <v-btn
      v-if="marker"
      small
      class="mt-10 mr-12"
      fab
      absolute
      top
      right
      @click="panToMarker"
    >
      <v-icon>mdi-crosshairs-gps</v-icon>
    </v-btn>
    <v-btn
      :loading="$apollo.loading"
      small
      class="mt-10"
      fab
      absolute
      top
      right
      @click="refresh"
    >
      <v-icon>mdi-refresh</v-icon>
    </v-btn>

    <div :class="$style.container">
      <div
        ref="map"
        :class="$style.map"
        @click="() => isFooterExpanded && expandFooter()"
      ></div>
      <div :class="$style.noLocation" v-if="trip && !trip.current_location">
        <v-icon>mdi-map-marker-off</v-icon>
        <span class="ml-4 grey--text text--darken-1"
          >Senaste position hittades inte...</span
        >
      </div>
    </div>

    <v-footer fixed elevation="5" padless tile v-if="trip">
      <v-expand-transition>
        <v-sheet
          tile
          width="100%"
          scrollable
          :key="footerHeight"
          :height="footerHeight"
          @click="expandFooter"
        >
          <OverviewListItem
            :show-via-places="false"
            elevation="0"
            v-if="trip"
            :bus="trip"
          >
            <div class="d-flex flex-column align-end">
              <span class="caption grey--text text--darken-5">
                Senaste positionering
              </span>
              <span
                v-if="trip.current_location && trip.current_location.timestamp"
                class="body-2 grey--text text--darken-2"
                >{{ trip.current_location.timestamp | formatTime }}</span
              >
              <span class="body-2 grey--text text--darken-2" v-else>
                -
              </span>
            </div>
          </OverviewListItem>
          <v-card ref="scroll" flat tile :style="scrollFooterStyle" v-if="trip">
            <PlaceItem
              :place="place"
              v-for="(place, $i) in trip.places"
              :is-last="$i === trip.places.length - 1"
              :key="$i"
            />
          </v-card>
        </v-sheet>
      </v-expand-transition>
    </v-footer>
  </v-content>
</template>

<script>
import OverviewListItem from '@/components/OverviewListItem'
import PlaceItem from '@/components/PlaceItem'
import gql from 'graphql-tag'
import { format } from 'date-fns'

const DEFAULT_FOOTER_HEIGHT = 200

export default {
  name: 'MapView',

  components: {
    OverviewListItem,
    PlaceItem
  },

  data: () => ({
    trip: null,
    map: null,
    marker: null,
    isFooterExpanded: false,
    footerHeight: DEFAULT_FOOTER_HEIGHT
  }),

  filters: {
    formatTime: date => format(new Date(date), 'yyyy-MM-dd HH:mm')
  },

  mounted() {
    this.$apollo.queries.trip.skip = false
  },

  apollo: {
    trip: {
      query: gql`
        query GetTrip($uuid: String!) {
          trip(uuid: $uuid) {
            train
            type
            current_location {
              timestamp
              longitude
              latitude
            }
            places {
              eta
              fill_percent
              done
              place
            }
          }
        }
      `,
      variables() {
        return {
          uuid: this.$route.params.trip_uuid
        }
      },
      skip: true,
      manual: true,
      async result({ data: { trip = null } }) {
        if (!trip) return

        if (trip.current_location) {
          if (!this.map) {
            await this.initMap()
          }

          const position = this.createLatLng(trip.current_location)
          this.initMarker(position)
          this.marker.setMap(this.map)
          this.panToMarker()
        }

        const lastPlaceIndex = trip.places.length - 1
        this.trip = {
          ...trip,
          to: trip.places[lastPlaceIndex].place,
          eta: trip.places[lastPlaceIndex].eta,
          via: trip.places
            .filter((x, i) => (i === i) !== 0 || i !== lastPlaceIndex)
            .map(x => x.place)
        }
      }
    }
  },

  computed: {
    scrollFooterStyle() {
      return {
        overflowY: this.isFooterExpanded ? 'auto' : 'hidden',
        maxHeight: this.footerHeight - 92 + 'px'
      }
    }
  },

  methods: {
    async initMap() {
      this.map = await new window.google.maps.Map(this.$refs.map, {
        center: this.createLatLng({ latitude: 0, longitude: 0 }),
        zoom: 15,
        streetViewControl: false,
        fullscreenControl: false,
        mapTypeControl: false,
        zoomControl: false
      })
    },

    initMarker(position) {
      const icon = require('@/assets/marker.png')
      this.marker = new window.google.maps.Marker({
        icon,
        position
      })
    },

    createLatLng({ latitude, longitude }) {
      return new window.google.maps.LatLng(latitude, longitude)
    },

    refresh() {
      this.$apollo.queries.trip.refetch()
    },

    expandFooter() {
      this.$refs.scroll.scrollTop
      if (this.footerHeight === DEFAULT_FOOTER_HEIGHT) {
        this.footerHeight = window.innerHeight - 56
        this.isFooterExpanded = true
      } else {
        this.footerHeight = DEFAULT_FOOTER_HEIGHT
        this.isFooterExpanded = false
      }
    },

    panToMarker() {
      this.map.panTo(this.marker.position)
    }
  }
}
</script>

<style module>
.container {
  height: calc(100vh - 200px);
  position: relative;
}

.map {
  width: 100%;
  height: 100%;
}

.noLocation {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 3;
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
}

.scrollContent {
  overflow-y: auto;
}
</style>
